import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SF_SURV_QUES_LIST_SRCH, SG_SURV_QUES_LIST } from "./services/PtliSurvQuesMgmtService";
import { PTLI0418001Q } from "../qustnr/PTLI0418001Q";
import { PTLI0418005Q } from "../qustnr/PTLI0418005Q";

/*
 * Survey Question Management List
 * !== 설문 질문 목록 ==!
 */

export const PTLI0419001Q = (props: any): JSX.Element => {
    const { callback, qestnrId } = props;
    const { pgeUid, menuId } = usePage({
        pgeUid: "ptli0419001q",
        auth: ["auth_ptli0015", "auth_ptli0060", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0024" }, // 업무구분
            { area: "comnCd", comnCd: "COM0052" }, // 포털유형
            { area: "comnCd", comnCd: "PTL0061" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survQuesListSrch: useForm({
            defaultSchema: SF_SURV_QUES_LIST_SRCH,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survQuesList: useGrid({
            defaultSchema: SG_SURV_QUES_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getSurvQuesList: useFetch({
            api: (page = grid.survQuesList.page) => {
                form.survQuesListSrch.setValue("qestnrId", qestnrId);
                return APIS.getSurvQuesList(form.survQuesListSrch.getValues(), page, grid.survQuesList.size);
            },
            enabled: comnUtils.isEmpty(form.survQuesListSrch.errors),
            key: [grid.survQuesList.page, grid.survQuesList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.survQuesListSrch.getValues(),
                    page: grid.survQuesList.page,
                    size: grid.survQuesList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),
    };
    const render = {
        cell: {
            qustnrTtle: (props: any) => {
                const { rowValues, value } = props;
                if (callback) {
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                callback(rowValues);
                            }}
                        >
                            {value}
                        </a>
                    );
                } else {
                    const { rowValues, value } = props;
                    return <Link to={`${URLS.ptli0419004q}/${rowValues.qestnrId}/${rowValues.quesId}`}>{value}</Link>;
                }
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onSrchBtnClick: () => {
            grid.survQuesList.setPage(0);
            if (callback) {
                form.survQuesListSrch.setValue("qestnrId", qestnrId);
            }
            fetch.getSurvQuesList.fetch(0);
        },

        onNewBtnClick: () => navigate(URLS.ptli0419002s),
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick();
    }, []);

    return (
        <Page pgeUid={pgeUid} info={true}>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survQuesListSrch.schema.bsopClsfCd} />
                            <Group.Control {...form.survQuesListSrch.schema.ptlTpCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.survQuesListSrch.schema.qustnrTtle}
                                rightButton={{
                                    icon: "search",
                                    onClick: () => {
                                        const modalCallback = (item: any) => {
                                            if (!comnUtils.isEmpty(item)) {
                                                form.survQuesListSrch.setValue("qustnrTtle", item.qustnrTtle);
                                                form.survQuesListSrch.setValue("qestnrId", item.qestnrId);
                                            }
                                            modal.closeModal();
                                        };

                                        modal.openModal({
                                            draggable: true,
                                            size: "md",
                                            content: <PTLI0418005Q callback={modalCallback} />,
                                        });
                                    },
                                }}
                            />
                            <Group.Control {...form.survQuesListSrch.schema.quesCn} />
                            <Group.Control {...form.survQuesListSrch.schema.rgsrUsr} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button
                                role={"reset"}
                                onClick={() => {
                                    form.survQuesListSrch.reset();
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.survQuesList.grid}
                            data={fetch.getSurvQuesList.data?.survQuesList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
